import { Icon } from '@assembly-js/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { SearchIcon } from 'src/components/icons';

import {
  getSettingsSearchOptionId,
  SETTINGS_SEARCH_RESULTS_ID,
  useSettingsSearch,
} from './useSettingsSearch';

/** The "Search settings" field under Back to app; its results replace the sidebar's sections. */
export const SettingsSearchField = () => {
  const { t } = useTranslation();
  const {
    query,
    setQuery,
    isSearching,
    options,
    activeIndex,
    setActiveIndex,
    select,
  } = useSettingsSearch();

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex(Math.min(activeIndex + 1, options.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex(Math.max(activeIndex - 1, 0));
        break;
      case 'Enter': {
        const option = options.at(activeIndex);
        if (option) {
          event.preventDefault();
          select(option);
        }
        break;
      }
      case 'Escape':
        if (query) {
          event.preventDefault();
          setQuery('');
        }
        break;
      default:
        break;
    }
  };

  return (
    <div className="box-border flex h-[30px] items-center gap-1.5 rounded bg-white px-2 shadow-[inset_0_0_0_1px_var(--color-gray-200)]">
      <SearchIcon className="h-3.5 w-3.5 shrink-0 text-secondary" />
      <input
        type="text"
        role="combobox"
        aria-label={t(($) => $.page_settings.search.label)}
        aria-expanded={isSearching}
        aria-controls={SETTINGS_SEARCH_RESULTS_ID}
        aria-autocomplete="list"
        aria-activedescendant={
          isSearching && options.length > 0
            ? getSettingsSearchOptionId(activeIndex)
            : undefined
        }
        placeholder={t(($) => $.page_settings.search.placeholder)}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={handleKeyDown}
        className="h-full min-w-0 flex-1 border-0 bg-transparent p-0 text-[13px] text-black-heading outline-none"
      />
      {query && (
        <button
          type="button"
          aria-label={t(($) => $.page_settings.search.clear)}
          onClick={() => setQuery('')}
          className="grid h-5 w-5 shrink-0 cursor-pointer place-items-center rounded border-0 bg-transparent p-0 text-secondary hover:text-black-heading"
        >
          <Icon icon="Close" className="h-3 w-3" />
        </button>
      )}
    </div>
  );
};
